import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ScanBarcode, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApi } from "../api";
import { useCurrentUser } from "../auth";
import RequirePermission from "../components/RequirePermission";
import {
  PRINTER_LIST_PERMISSION,
  PRINTER_JOB_LABEL_PERMISSION,
} from "../constants/permissions";
import { BASE_LANGUAGE_OPTIONS } from "../constants/languages";
import { analyzeStatementXmlSegments } from "../components/statement-nodes/parse";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Empty,
  Field,
  Input,
  Loading,
  Select,
  SelectItem,
  Status,
} from "../components/ui";
import {
  LoadError,
  printerIsOnline,
  useResource,
  type Printer,
} from "./settings/shared";
import { usableLabelText as usableText } from "./settings/printing-safety";
import "./settings/settings.css";

type Product = {
  id: string;
  name: string;
  sku?: string;
  ean?: string;
  base_language?: string;
};
type Translation = {
  language: string;
  latest_text?: string;
  latest_version: number;
};
type Outcome = {
  id: string;
  product: string;
  language: string;
  printer: string;
  success: boolean;
  message: string;
  time: string;
};
const SETUP_KEY = "valid-labels:print-setup:v1";
const readSetup = () => {
  try {
    return JSON.parse(localStorage.getItem(SETUP_KEY) || "{}");
  } catch {
    return {};
  }
};
const normalizeCode = (value?: string | null) =>
  (value || "").trim().toLowerCase();

export default function Printing() {
  const { currentUser, impersonatedOrgId } = useCurrentUser();
  return (
    <RequirePermission permission={PRINTER_LIST_PERMISSION}>
      <PrintWorkspace
        key={impersonatedOrgId || currentUser?.org_id || "none"}
      />
    </RequirePermission>
  );
}
function PrintWorkspace() {
  const { t, i18n } = useTranslation("settings");
  const api = useApi();
  const { permissions } = useCurrentUser();
  const canPrint = permissions.includes(PRINTER_JOB_LABEL_PERMISSION);
  const printers = useResource<Printer[]>("/printers");
  const [params] = useSearchParams();
  const [printerId, setPrinterId] = useState(() => readSetup().printerId || "");
  const [language, setLanguage] = useState(() => readSetup().language || "en");
  const [continuous, setContinuous] = useState(false);
  const [copies, setCopies] = useState(1);
  const [query, setQuery] = useState(params.get("query") || "");
  const [matches, setMatches] = useState<Product[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [searched, setSearched] = useState(false);
  const [history, setHistory] = useState<Outcome[]>([]);
  const [now, setNow] = useState(Date.now());
  const [waiting, setWaiting] = useState(0);
  const locked = useRef(false);
  const scan = useRef<HTMLInputElement>(null);
  const scanQueue = useRef<string[]>([]);
  const autoFind = useRef(Boolean(params.get("query")));
  const selectedPrinter = printers.data?.find(
    (printer) => printer.id === printerId,
  );
  const ready = Boolean(
    selectedPrinter && printerIsOnline(selectedPrinter, now),
  );
  const selectedTranslation = translations.find(
    (item) => item.language === language,
  );
  const parsed = useMemo(
    () => analyzeStatementXmlSegments(selectedTranslation?.latest_text || ""),
    [selectedTranslation?.latest_text],
  );
  const languageName = (code: string) =>
    new Intl.DisplayNames([i18n.language], { type: "language" }).of(code) ||
    code.toUpperCase();
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
      void printers.reload();
    }, 30000);
    return () => clearInterval(timer);
  }, [printers.reload]);
  useEffect(() => {
    if (!printers.data?.length) return;
    // A remembered printer that was removed or went offline is not a choice.
    const remembered = printers.data.find((printer) => printer.id === printerId);
    if (!remembered || !printerIsOnline(remembered))
      setPrinterId(
        printers.data.find((printer) => printerIsOnline(printer))?.id || "",
      );
  }, [printers.data]);
  useEffect(() => {
    try {
      localStorage.setItem(SETUP_KEY, JSON.stringify({ printerId, language }));
    } catch {
      // Only a convenience: setup is chosen again on the next visit.
    }
  }, [printerId, language]);
  useEffect(() => {
    if (continuous && ready) scan.current?.focus();
  }, [continuous, ready]);
  const remember = (item: Omit<Outcome, "id" | "time">, jobId?: string) =>
    setHistory((prev) =>
      [
        {
          ...item,
          id: jobId || crypto.randomUUID(),
          time: new Date().toISOString(),
        },
        ...prev,
      ].slice(0, 20),
    );
  const submitJob = async (record: Product, items: Translation[]) => {
    if (!canPrint) throw new Error(t("common.denied"));
    const fresh: Printer[] = await api.get("/printers");
    const destination = fresh.find((printer) => printer.id === printerId);
    if (!destination || !printerIsOnline(destination))
      throw new Error(t("print.offline"));
    if (
      !usableText(items.find((item) => item.language === language)?.latest_text)
    )
      throw new Error(
        t("print.missingText", { language: languageName(language) }),
      );
    const response = await api.post(`/printers/${printerId}/jobs/label`, {
      product_id: record.id,
      language,
      copies: continuous ? 1 : copies,
      correlation_id: `ui-${crypto.randomUUID()}`,
    });
    const feedback = t("print.sent", {
      product: record.name,
      printer: destination.display_name,
      language: languageName(language),
    });
    setMessage(feedback);
    remember(
      {
        product: record.name,
        printer: destination.display_name,
        language,
        success: true,
        message: t("print.queued"),
      },
      response?.job_id,
    );
  };
  const loadProduct = async (record: Product, autoPrint: boolean) => {
    const items: Translation[] = await api.get(
      `/products/${record.id}/translations?includeText=true`,
    );
    setProduct(record);
    setTranslations(items);
    setMatches([]);
    if (autoPrint) await submitJob(record, items);
  };
  const reportError = (e: unknown, record?: Product) => {
    const problem = e as { status?: number; message?: string };
    // Client-side prerequisite messages are localized. Server details can contain
    // XML/internal IDs; point to the product rather than rendering a raw trace.
    const reason = problem.status
      ? problem.status === 404
        ? t("print.noMatch")
        : problem.status === 400 || problem.status === 422
          ? t("print.invalidText")
          : t("print.failed")
      : problem.message || t("print.failed");
    setError(reason);
    if (continuous)
      remember({
        product: record?.name || query,
        language,
        printer: selectedPrinter?.display_name || "",
        success: false,
        message: reason,
      });
  };
  // Scanners type faster than a print request finishes, so each code waits its
  // turn and the field stays usable. Only an exact code can print unattended.
  const printNextScans = async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    while (scanQueue.current.length) {
      const input = scanQueue.current.shift()!;
      setWaiting(scanQueue.current.length);
      setError("");
      setMessage("");
      setMatches([]);
      setSearched(true);
      let found: Product | undefined;
      try {
        const result: Product[] = await api.get(
          `/products?query=${encodeURIComponent(input)}&size=20&page=1`,
        );
        const code = normalizeCode(input);
        const exact = result.filter(
          (item) =>
            normalizeCode(item.ean) === code ||
            normalizeCode(item.sku) === code,
        );
        if (exact.length !== 1)
          throw new Error(
            t(exact.length ? "print.ambiguousMatch" : "print.noExactMatch"),
          );
        found = exact[0];
        await loadProduct(found, true);
      } catch (e) {
        reportError(e, found || ({ name: input } as Product));
      }
    }
    locked.current = false;
    setBusy(false);
  };
  const find = async (event: FormEvent) => {
    event.preventDefault();
    if (continuous) {
      if (!query.trim() || !ready || !canPrint) return;
      scanQueue.current.push(query.trim());
      setWaiting(scanQueue.current.length);
      setQuery("");
      void printNextScans();
      return;
    }
    if (locked.current || !query.trim()) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    setMatches([]);
    setProduct(null);
    setTranslations([]);
    setSearched(true);
    let found: Product | undefined;
    try {
      const input = query.trim();
      // A numeric SKU can look exactly like an EAN. Search the same catalog
      // fields for every input and ask the user when more than one product fits.
      const result: Product[] = await api.get(
        `/products?query=${encodeURIComponent(input)}&size=20&page=1`,
      );
      if (!result.length) {
        setError(t("print.noMatch"));
      } else if (result.length === 1) {
        found = result[0];
        await loadProduct(found, false);
      } else {
        setMatches(result);
      }
    } catch (e) {
      reportError(e, found);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!autoFind.current) return;
    autoFind.current = false;
    void find({ preventDefault() {} } as FormEvent);
  }, []);
  const selectProduct = async (record: Product) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await loadProduct(record, false);
    } catch (e) {
      reportError(e, record);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const print = async () => {
    if (!product || locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await submitJob(product, translations);
    } catch (e) {
      reportError(e, product);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const setupCard = (
    <Card aria-label={t("print.setup")}>
      <CardHeader title={t("print.setup")}>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/settings?section=printers">
            {t("print.managePrinters")}
          </Link>
        </Button>
      </CardHeader>
      <CardBody className="print-setup">
        <Field label={t("print.destination")} htmlFor="print-printer">
          <Select
            id="print-printer"
            value={printerId}
            onValueChange={(value) => setPrinterId(value)}
            disabled={busy}
          >
            <SelectItem value="">{t("print.choosePrinter")}</SelectItem>
            {printers.data?.map((printer) => (
              <SelectItem key={printer.id} value={printer.id}>
                {printer.display_name} ·{" "}
                {t(
                  printerIsOnline(printer, now)
                    ? "printers.online"
                    : "printers.offline",
                )}
              </SelectItem>
            ))}
          </Select>
        </Field>
        <Field label={t("print.language")} htmlFor="print-language">
          <Select
            id="print-language"
            value={language}
            onValueChange={(value) => setLanguage(value)}
            disabled={busy}
          >
            {BASE_LANGUAGE_OPTIONS.map((item) => (
              <SelectItem key={item.code} value={item.code}>
                {languageName(item.code)} ({item.code.toUpperCase()})
              </SelectItem>
            ))}
          </Select>
        </Field>
        {!continuous && (
          <Field label={t("print.copies")} htmlFor="print-copies">
            <Input
              id="print-copies"
              type="number"
              min="1"
              max="10"
              step="1"
              value={copies}
              onChange={(e) => setCopies(Number(e.target.value))}
              disabled={busy}
            />
          </Field>
        )}
      </CardBody>
    </Card>
  );
  return (
    <div className="print-page">
      <header className="settings-section-heading">
        <h1>{t("print.title")}</h1>
        <div className="vl-segmented" role="group" aria-label={t("print.mode")}>
          <button
            type="button"
            aria-pressed={!continuous}
            disabled={busy}
            onClick={() => {
              setContinuous(false);
              setMessage("");
              setError("");
            }}
          >
            <Tag aria-hidden="true" />
            {t("print.single")}
          </button>
          <button
            type="button"
            aria-pressed={continuous}
            disabled={busy}
            onClick={() => {
              setContinuous(true);
              setMessage("");
              setError("");
            }}
          >
            <ScanBarcode aria-hidden="true" />
            {t("print.continuous")}
          </button>
        </div>
      </header>
      <LoadError {...printers} />
      {printers.loading && !printers.data && (
        <Loading label={t("common.loading")} />
      )}
      {!printers.loading && printers.data?.length === 0 && (
        <Card>
          <Empty
            title={t("printers.empty")}
            description={t("printers.emptyHint")}
          >
            <Button asChild>
              <Link to="/settings?section=printers">
                {t("printers.connect")}
              </Link>
            </Button>
          </Empty>
        </Card>
      )}
      {!canPrint && <Alert variant="info">{t("print.noPermission")}</Alert>}
      {selectedPrinter && !ready && (
        <Alert variant="warning">
          {t("print.offline")}{" "}
          <Link to="/settings?section=printers">
            {t("print.managePrinters")}
          </Link>
        </Alert>
      )}
      <div className="print-workspace">
        <div className="print-main">
          <Card>
            <CardBody>
              <form className="print-search" onSubmit={find}>
                <Field
                  htmlFor="print-query"
                  label={continuous ? t("print.scan") : t("print.search")}
                  hint={
                    continuous ? t("print.continuousHint") : t("print.searchHint")
                  }
                >
                  <Input
                    ref={scan}
                    id="print-query"
                    className="print-query"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoComplete="off"
                    disabled={
                      continuous ? !ready || !canPrint : busy
                    }
                    placeholder={t("print.searchPlaceholder")}
                  />
                </Field>
                <Button
                  type="submit"
                  variant={product && !continuous ? "outline" : "default"}
                  disabled={
                    !query.trim() ||
                    (continuous ? !ready || !canPrint : busy)
                  }
                >
                  {busy && !continuous
                    ? t("print.working")
                    : continuous
                      ? t("print.findAndPrint")
                      : t("print.find")}
                </Button>
              </form>
              {continuous && (
                <p className="settings-muted print-continuous-state">
                  {ready
                    ? t("print.continuousReady", {
                        printer: selectedPrinter?.display_name,
                        language: languageName(language),
                      })
                    : t("print.continuousPaused")}
                  {waiting > 0 &&
                    ` ${t("print.queueWaiting", { count: waiting })}`}
                </p>
              )}
            </CardBody>
          </Card>
          {error && <Alert variant="error">{error}</Alert>}
          {message && <Alert variant="success">{message}</Alert>}
          {matches.length > 0 && (
            <Card aria-label={t("print.chooseProduct")}>
              <CardHeader title={t("print.chooseProduct")} />
              <CardBody className="print-matches">
                {matches.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => void selectProduct(item)}
                    disabled={busy}
                  >
                    <strong>{item.name}</strong>
                    <span>
                      {[item.sku, item.ean].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))}
              </CardBody>
            </Card>
          )}
          {product && (
            <Card className="print-preview" aria-label={product.name}>
              <div className="print-preview-head">
                <div>
                  <h2>{product.name}</h2>
                  <p className="settings-muted">
                    {[
                      [product.sku, product.ean].filter(Boolean).join(" · "),
                      languageName(language),
                      selectedTranslation &&
                        t("print.version", {
                          version: selectedTranslation.latest_version,
                        }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <div className="work-actions">
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/products/${product.id}?language=${language}`}>
                      {t("print.openProduct")}
                    </Link>
                  </Button>
                  <Button
                    variant={continuous ? "outline" : "default"}
                    disabled={
                      busy ||
                      !ready ||
                      !canPrint ||
                      !usableText(selectedTranslation?.latest_text) ||
                      (!continuous &&
                        (!Number.isInteger(copies) ||
                          copies < 1 ||
                          copies > 10))
                    }
                    onClick={() => void print()}
                  >
                    {t("print.printLabel")}
                  </Button>
                </div>
              </div>
              {!usableText(selectedTranslation?.latest_text) ? (
                <div className="print-preview-body">
                  <Alert variant="warning">
                    {selectedTranslation?.latest_text
                      ? t("print.invalidText")
                      : t("print.missingText", {
                          language: languageName(language),
                        })}
                  </Alert>
                </div>
              ) : (
                <div className="print-statements" lang={language}>
                  {parsed.segments
                    .filter(
                      (segment) =>
                        segment.kind === "node" || segment.text.trim(),
                    )
                    .map((segment, index) => (
                      <div className="print-statement" key={index}>
                        <span className="print-code">
                          {segment.kind === "node" ? segment.node.code : ""}
                        </span>
                        <p>
                          {segment.kind === "node"
                            ? segment.node.text
                            : segment.text}
                        </p>
                      </div>
                    ))}
                </div>
              )}
              <p className="settings-muted print-preview-note">
                {t("print.latestSaved")}
              </p>
            </Card>
          )}
          {!product && !matches.length && !searched && (
            <Card className="print-instruction">
              <h2>{t("print.start")}</h2>
              <p className="settings-muted">{t("print.startHint")}</p>
            </Card>
          )}
        </div>
        <aside className="print-rail">{setupCard}</aside>
      </div>
      {history.length > 0 && (
        <section className="settings-section print-history">
          <h2>{t("print.recent")}</h2>
          <table className="settings-table">
            <caption className="sr-only">{t("print.recent")}</caption>
            <thead>
              <tr>
                <th>{t("print.product")}</th>
                <th>{t("print.language")}</th>
                <th>{t("print.destination")}</th>
                <th>{t("print.outcome")}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id}>
                  <td data-label={t("print.product")}>
                    <strong>{item.product}</strong>
                    <span className="settings-muted settings-block">
                      {new Date(item.time).toLocaleTimeString(i18n.language)}
                    </span>
                  </td>
                  <td data-label={t("print.language")}>
                    {languageName(item.language)}
                  </td>
                  <td data-label={t("print.destination")}>{item.printer}</td>
                  <td data-label={t("print.outcome")}>
                    <Status tone={item.success ? "success" : "danger"}>
                      {item.message}
                    </Status>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
